import { useEffect, useRef, useState } from 'react';

import { ButtonByTeam } from 'components/button/ButtonByTeam';
import { Button, Row, Spacing, Tooltip, Typography, blue, neutral, neutralTrans } from 'thesis-ui';

import { CollectionLabel } from 'modules/device-command/components';

import { SecureTokenCard, SecureTokenContainer } from '../styles/api-key';

import { ReactComponent as CopyKeyIcon } from 'assets/icons/copy-key.svg';
import { ReactComponent as GenerateKeyIcon } from 'assets/icons/generate-key.svg';

import withDeviceRole from 'hooks/withDeviceRole';

import { APIKeyItem } from '../components/APIKeyItem';
import { useDeviceAPIKeyController } from '../core';

const DeviceAPIKey = () => {
  const { tokenKey, onCopyTokenKey, onGenerateNewKey } = useDeviceAPIKeyController();
  const containerRef = useRef<HTMLDivElement>(null);
  const [clientWidth, setClientWidth] = useState<number | string>('100%');

  useEffect(() => {
    if (containerRef.current) {
      setClientWidth(containerRef.current?.clientWidth);
    }
  }, [containerRef.current]);

  return (
    <Row
      style={{
        maxWidth: 1064,
      }}
    >
      <SecureTokenContainer ref={containerRef} direction="column">
        <Typography variant="h5" weight={600}>
          Secure token key
        </Typography>
        <Row
          align="center"
          style={{
            marginTop: 6,
          }}
        >
          <SecureTokenCard align="center">
            <Typography variant="h4" weight={700}>
              {tokenKey}
            </Typography>
          </SecureTokenCard>
          <Tooltip content="Copy" arrow={false} placement="top">
            <Spacing spacing="sm" type="margin" placement="left" style={{ width: 'fit-content' }}>
              <Button
                onClick={onCopyTokenKey}
                icon={<CopyKeyIcon />}
                placementIcon="left"
                style={{
                  width: 44,
                  height: 52,
                  border: 'none',
                }}
              />
            </Spacing>
          </Tooltip>
        </Row>
        <Spacing
          spacing="xl"
          type="padding"
          placement={['top', 'bottom']}
          style={{ width: '100%', borderBottom: `1px solid ${neutralTrans['neutralTrans-10']}` }}
        >
          <ButtonByTeam
            onClick={onGenerateNewKey}
            icon={<GenerateKeyIcon style={{ marginRight: 6 }} />}
            title="Generate new key"
            rootStyle={{
              width: 'fit-content',
            }}
          />
        </Spacing>
        <Spacing spacing="xl" type="margin" placement={['top', 'bottom']}>
          <Typography variant="h5" weight={600}>
            API requests
          </Typography>
        </Spacing>
        <APIKeyItem
          clientWidth={clientWidth}
          title="Write a Channel Feed"
          label={
            <Row align="center">
              <Typography weight={600}>Body</Typography>
              <Spacing spacing="xxs" placement="left" type="margin">
                <Typography color={neutral['neutral-7']}>raw (json)</Typography>
              </Spacing>
            </Row>
          }
          method="POST"
          url="api/device-records/update"
          apiKey={tokenKey}
          code={`[{
        "timestamp": 1698811030,    
        "data": "test message", // can be "text","number","encode base64 file",
        "data_type": "text", // can be "text","number","file",
        "label": "example",
        "file_name": "tracelium-connect.png" // only have when "data_type"="file"
}]`}
        />
        <Row
          style={{
            marginTop: 40,
          }}
        >
          <APIKeyItem
            clientWidth={clientWidth}
            title="Poll available commands"
            method="GET"
            url="api/commands/poll"
            apiKey={tokenKey}
            label={<Typography weight={600}>Response example</Typography>}
            code={`[
        {name: "Command 1", data: "0x010A020000"},
        {name: "Command 2", data: "Alarm-interval:100"},
]`}
          />
        </Row>
      </SecureTokenContainer>
      <Row
        style={{
          minWidth: 360,
          width: 360,
        }}
      >
        <CollectionLabel
          label={
            <Row direction="column" justify="start">
              <Typography
                color={blue['blue-6']}
                style={{
                  textAlign: 'start',
                  marginBottom: 10,
                }}
              >
                Use this key to write data to a device or read data from a device.
              </Typography>
              <Typography
                color={blue['blue-6']}
                style={{
                  textAlign: 'start',
                }}
              >
                The Secure token key is auto-generated when you create a new device. If you think
                your key has been compromised, click “Generate new key” button.{' '}
              </Typography>
            </Row>
          }
          type="info"
        />
      </Row>
    </Row>
  );
};

export default withDeviceRole(DeviceAPIKey);
